import { useState } from 'react';
import { Modal, TextInput, Textarea, NumberInput, Stack, Group, Button, Alert, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import ImageUpload from '../../components/ImageUpload';
import { api } from '../../api/client';
import { useAuth, canWrite } from '../../store/auth';

export interface OrderPart {
  id: number;
  orderId: number;
  partUid: string;
  name: string;
  imagePath: string | null;
  quantity: number;
  sharepointLocation: string | null;
  notes: string | null;
}

export interface PendingPart {
  id: number; // negative temp id
  name: string;
  imagePath: string | null;
  quantity: number;
  sharepointLocation: string | null;
  notes: string | null;
}

export default function OrderPartModal({
  orderId,
  initial,
  onClose,
  onDone,
  onLocalSave,
  onLocalDelete,
}: {
  orderId?: number;
  initial: OrderPart | PendingPart | null;
  onClose: () => void;
  onDone: () => void;
  onLocalSave?: (p: Omit<PendingPart, 'id'>) => void;
  onLocalDelete?: () => void;
}) {
  const { me } = useAuth();
  const writable = canWrite(me?.role);
  const localMode = !!onLocalSave;

  const [name, setName] = useState(initial?.name ?? '');
  const [image, setImage] = useState<string | null>(initial?.imagePath ?? null);
  const [quantity, setQuantity] = useState<number | string>(initial?.quantity ?? 1);
  const [sharepoint, setSharepoint] = useState(initial?.sharepointLocation ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setErr(null);
    if (!name.trim()) return setErr('Name is required.');
    const qty = Math.max(1, Math.round(Number(quantity) || 1));
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        imagePath: image,
        quantity: qty,
        sharepointLocation: sharepoint.trim() || null,
        notes: notes.trim() || null,
      };
      if (localMode) {
        onLocalSave!(body);
        return;
      }
      if (initial && orderId) {
        await api.put(`/orders/${orderId}/parts/${(initial as OrderPart).id}`, body);
      } else if (orderId) {
        await api.post(`/orders/${orderId}/parts`, body);
      }
      notifications.show({ message: 'Part saved.', color: 'green' });
      onDone();
    } catch (e: any) {
      setErr(e.message ?? 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  function confirmDelete() {
    if (!initial) return;
    const label = (initial as OrderPart).partUid ?? (initial as PendingPart).name;
    modals.openConfirmModal({
      title: `Delete ${label}?`,
      children: 'This part will be removed from the order.',
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        if (onLocalDelete) { onLocalDelete(); return; }
        try {
          if (!orderId || !initial) return;
          await api.delete(`/orders/${orderId}/parts/${(initial as OrderPart).id}`);
          notifications.show({ message: 'Part deleted.', color: 'gray' });
          onDone();
        } catch (e: any) {
          notifications.show({ message: e.message ?? 'Delete failed', color: 'red' });
        }
      },
    });
  }

  return (
    <Modal opened onClose={onClose} size="lg" title={<span className="modal-title-custom">{initial ? `Edit Part ${('partUid' in (initial as any)) ? (initial as OrderPart).partUid : (initial as PendingPart).name}` : 'Add Part'}</span>}>
      <Stack>
        <ImageUpload entity="parts" value={image} onChange={setImage} />
        <TextInput
          data-tour="part-name"
          label="Part Name"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          required
          maxLength={120}
          disabled={!writable}
        />
        {initial && 'partUid' in (initial as any) && (
          <TextInput label="Part UID" value={(initial as OrderPart).partUid} disabled readOnly />
        )}
        {(!initial || !('partUid' in (initial as any))) && (
          <Text size="xs" c="dimmed">
            Part UID will be generated as <code>&lt;OrderUID&gt;-P###</code> {localMode ? 'when the order is saved.' : 'on save.'}
          </Text>
        )}
        <NumberInput
          data-tour="part-qty"
          label="Quantity"
          value={quantity}
          onChange={setQuantity}
          min={1}
          step={1}
          required
          disabled={!writable}
        />
        <TextInput
          label="SharePoint Location"
          placeholder="Path to files"
          value={sharepoint}
          onChange={(e) => setSharepoint(e.currentTarget.value)}
          disabled={!writable}
        />
        <Textarea
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          maxLength={500}
          autosize
          minRows={3}
          description={`${notes.length}/500`}
          disabled={!writable}
        />
        {err && <Alert color="red">{err}</Alert>}
        <Group justify="space-between" mt="md">
          <div>
            {initial && writable && <Button color="red" variant="light" onClick={confirmDelete}>Delete</Button>}
          </div>
          <Group>
            <Button variant="default" onClick={onClose}>Cancel</Button>
            {writable && <Button data-tour="part-save" onClick={save} loading={busy}>Save</Button>}
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
